import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "antd";
import type React from "react";
import styled from "styled-components";
import type { MonetaryAmount } from "../../types/formTypes";
import TextField from "./TextField";

const FieldWrapper = styled.div`
  margin-bottom: 24px;
  text-align: left;
`;

const LabelRow = styled.label`
  display: inline-flex !important;
  flex-direction: row !important;
  align-items: center;
  gap: 4px;
  margin-bottom: 6px;
  font-weight: 700;
  font-size: 16px;
  cursor: text;
  text-align: left;
`;

const RequiredMark = styled.span`
  color: #d32f2f;
`;

const FieldsWrapper = styled.div`
  margin-top: 6px;
  width: 100%;
  box-sizing: border-box;

  input,
  .ant-select {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box !important;
  }
`;

export interface MonetaryAmountFieldProps {
  label: string;
  name: string;
  value: MonetaryAmount | undefined;
  onChange: (value: MonetaryAmount | undefined) => void;
  required?: boolean;
  tooltip?: string;
}

const MonetaryAmountField: React.FC<MonetaryAmountFieldProps> = ({
  label,
  name,
  value,
  onChange,
  required = false,
  tooltip,
}) => {
  const amount = value ?? { currency: "", value: "" };

  const updateField = (updates: Partial<MonetaryAmount>) => {
    const next = { ...amount, ...updates };
    const hasContent =
      (next.currency ?? "").trim() !== "" || (next.value ?? "").trim() !== "";
    onChange(hasContent ? next : undefined);
  };

  return (
    <FieldWrapper>
      <LabelRow htmlFor={name}>
        {label}
        {required && <RequiredMark>*</RequiredMark>}
        {tooltip && (
          <Tooltip title={tooltip}>
            <span style={{ display: "inline-flex", cursor: "help" }}>
              <FontAwesomeIcon
                icon={faCircleInfo}
                style={{
                  color: "var(--color-primary-desat)",
                  fontSize: "0.9rem",
                }}
              />
            </span>
          </Tooltip>
        )}
      </LabelRow>
      <FieldsWrapper>
        <TextField
          label="Currency"
          name={`${name}-currency`}
          value={amount.currency ?? ""}
          onChange={(v) => updateField({ currency: v })}
          tooltip="The currency in which the monetary amount is expressed. Use standard formats: ISO 4217 currency format, e.g. USD; Ticker symbol for cryptocurrencies, e.g. BTC; well known names for Local Exchange Trading Systems (LETS) and other currency types, e.g. Ithaca HOUR."
        />
        <TextField
          label="Value"
          name={`${name}-value`}
          value={amount.value ?? ""}
          onChange={(v) => updateField({ value: v })}
          tooltip="The cost in the named currency."
        />
      </FieldsWrapper>
    </FieldWrapper>
  );
};

export default MonetaryAmountField;
